/*
 * File: useStudentSectionData.ts
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  getStudentLedgerOptions,
  getStudentOptions,
  getStudentScheduleOptions,
} from '@drivingdoc/api-client/generated/react-query';
import type { CoursePeriod } from 'types/periods';
import { GetStudentResponse, LedgerEntry, StudentPeriodEnrollment } from 'types/students';

import { getDriveEnrollmentLabel } from 'pages/Admin/LegacyCalendar/util/getDriveEnrollmentLabel';

import { useDriveOverviewDialogContext } from '../../context/DriveOverviewDialogContext';
import { getDriveHistoryStats } from '../DriveHistory/getDriveHistorySummary';

interface UseStudentSectionDataParams {
  enrollment: CoursePeriod['period_enrollments'][0] | undefined;
}

/**
 * Custom hook that encapsulates all data fetching and transformation logic
 * for the StudentSection component
 */
export function useStudentSectionData({ enrollment }: UseStudentSectionDataParams) {
  const { period } = useDriveOverviewDialogContext();
  const courseId = period?.course_section.course_id ?? '';

  // Fetch detailed student information
  const studentQuery = useQuery({
    ...getStudentOptions({
      path: {
        id: enrollment?.student_user_id ?? '',
      },
    }),
    enabled: !!enrollment,
  });

  // Fetch student period enrollments for drive history
  const studentPeriods = useQuery({
    ...getStudentScheduleOptions({
      path: {
        id: enrollment?.student_user_id ?? '',
      },
    }),
    enabled: !!enrollment,
  });

  // Fetch student ledger for balance information
  const studentLedger = useQuery({
    ...getStudentLedgerOptions({
      path: {
        id: enrollment?.student_user_id ?? '',
      },
      query: {
        showVoided: false,
      },
    }),
    enabled: !!enrollment,
  });

  // Memoized computed values
  const studentUser = studentQuery.data as GetStudentResponse | undefined;
  const parents = studentUser?.student.parents ?? [];

  const allPeriodEnrollments = useMemo(
    () =>
      ((studentPeriods.data as StudentPeriodEnrollment[] | undefined) ?? []).filter(
        (periodEnrollment) => !courseId || periodEnrollment.course_period.course_section.course_id === courseId,
      ),
    [studentPeriods.data, courseId],
  );

  const driveLabel = enrollment ? getDriveEnrollmentLabel(enrollment, '#') : '';

  const driveHistoryStats = useMemo(() => {
    return getDriveHistoryStats(allPeriodEnrollments);
  }, [allPeriodEnrollments]);

  const balanceStatus = useMemo(() => {
    const ledgerData = studentLedger.data as LedgerEntry[] | undefined;
    if (!ledgerData || ledgerData.length === 0) {
      return { amount: 0, isOverdue: false };
    }

    // Get the last entry's balance (running total)
    const lastEntry = ledgerData[ledgerData.length - 1];
    const balance = Number(lastEntry.balance ?? 0);

    return {
      amount: balance,
      isOverdue: balance > 0, // Positive balance means money owed
    };
  }, [studentLedger.data]);

  return {
    studentUser,
    parents,
    driveLabel,
    allPeriodEnrollments,
    driveHistoryStats,
    balanceStatus,
    isLoading: studentQuery.isLoading || studentPeriods.isLoading || studentLedger.isLoading,
    isError: studentQuery.isError || studentPeriods.isError,
  };
}
